import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { Hotel, LogIn, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

export default function GuestLoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useGuestAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      const bookingSelection = sessionStorage.getItem('bookingSelection');
      if (bookingSelection) {
        navigate('/portal/booking');
      } else {
        navigate('/portal/dashboard');
      }
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!form.email || !form.password) {
      toast.error('Please enter email and password');
      return;
    }

    try {
      setLoading(true);
      const response = await guestPortalApi.login(form);
      
      // Use the context login function
      login(response.data);
      
      toast.success('Welcome back!');
      
      // Check if there's a pending booking
      const bookingSelection = sessionStorage.getItem('bookingSelection');
      if (bookingSelection) {
        navigate('/portal/booking');
      } else {
        navigate('/portal/dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4" data-testid="guest-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Hotel className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to manage your reservations</CardDescription>
        </CardHeader>
        <form onSubmit={handleLogin}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-button">
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <LogIn className="h-4 w-4 mr-2" />
              )}
              Sign In
            </Button>
            <div className="text-center text-sm">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/portal/register" className="text-primary hover:underline">
                Register
              </Link>
            </div>
            <Link to="/portal" className="text-sm text-muted-foreground hover:underline text-center">
              ← Back to Hotel Search
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
